import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ChatComponent, CopilotFacadeService } from '@brighthub/copilot';
import {
  ClientAccountsComponent,
  ClientAccountsStateService,
  ClientActionsComponent,
  ClientActionsStateService,
  ClientAiInsightsComponent,
  ClientAiInsightsStateService,
  ClientChatDemoComponent,
  ClientChatDemoStateService,
  ClientChatSuggestionsStateService,
  ClientChatThreadStateService,
  ClientDocumentsComponent,
  ClientDocumentsStateService,
  ClientFinancesComponent,
  ClientFinancesStateService,
  ClientJourneyComponent,
  ClientJourneyStateService,
  ClientListComponent,
  ClientProfileComponent,
  ClientQualityComponent,
  ClientQualityStateService,
  ClientResourcesComponent,
  ClientResourcesStateService,
  FaInviteStateService,
} from '@brightai/ui';
import { MenuItem } from 'primeng/api';
import { Menubar } from 'primeng/menubar';
import { Splitter } from 'primeng/splitter';
import { BrightaiLayoutComponent } from '../brightai-layout/brightai-layout.component';
import { BrightaiLayoutService } from '../brightai-layout.service';
import { ClientSessionService } from '@brighthub/uisession';
import { StoryDemoFacade } from '@brighthub/story';
import { TabBarComponent, TabItem } from '../tab-bar/tab-bar.component';

/** Tab id for the left-hand content area. Chat is docked in the right pane. */
export type MyClientsTabId = 'client-chat' | 'actions' | 'ai-insights' | 'resources' | 'finances' | 'accounts' | 'journey' | 'quality' | 'profile' | 'documents';

/**
 * Height for the content splitter so it does not grow with content.
 * Matches app chrome: 3.5rem shell header + 62px client-metadata (tab menubar is inside left pane).
 */
const MY_CLIENTS_SPLITTER_HEIGHT = 'calc(100vh - 3.5rem - 62px)';

@Component({
  selector: 'bai-my-clients-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgTemplateOutlet,
    ChatComponent,
    ClientAccountsComponent,
    ClientActionsComponent,
    ClientAiInsightsComponent,
    ClientChatDemoComponent,
    ClientDocumentsComponent,
    ClientFinancesComponent,
    ClientJourneyComponent,
    ClientListComponent,
    ClientProfileComponent,
    ClientQualityComponent,
    ClientResourcesComponent,
    BrightaiLayoutComponent,
    Menubar,
    Splitter,
    TabBarComponent,
  ],
  providers: [ClientAccountsStateService, ClientActionsStateService, ClientAiInsightsStateService, ClientChatDemoStateService, ClientChatSuggestionsStateService, ClientChatThreadStateService, ClientDocumentsStateService, ClientFinancesStateService, ClientJourneyStateService, ClientQualityStateService, ClientResourcesStateService, FaInviteStateService],
  templateUrl: './my-clients-layout.component.html',
  styleUrl: './my-clients-layout.component.scss',
})
export class MyClientsLayoutComponent implements OnDestroy {
  layoutService = inject(BrightaiLayoutService);
  clientSessionService = inject(ClientSessionService);
  private readonly _injector = inject(Injector);
  private readonly _storyFacade = this.resolveStoryFacade();
  private readonly _aiInsightsState = inject(ClientAiInsightsStateService);
  private readonly _actionsState = inject(ClientActionsStateService);
  private _copilotFacadeService = inject(CopilotFacadeService);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  collapsed = computed(() => this.layoutService.collapsed());
  copilotCollapsed = computed(() => this.layoutService.copilotCollapsed());

  /** Active tab in the tab-style menubar; drives which content shows left of chat. */
  readonly activeTab = signal<MyClientsTabId>('client-chat');

  /** Static tab definitions for the overflow-aware tab bar. */
  readonly allTabs: TabItem[] = [
    { id: 'client-chat',  label: 'Client Chat',  icon: 'pi pi-comments' },
    { id: 'actions',      label: 'Actions',      icon: 'pi pi-list-check' },
    { id: 'ai-insights',  label: 'AI Insights',  icon: 'pi pi-microchip-ai' },
    { id: 'resources',    label: 'Resources',    icon: 'pi pi-briefcase' },
    { id: 'finances',     label: 'Finances',     icon: 'pi pi-chart-line' },
    { id: 'accounts',     label: 'Accounts',     icon: 'pi pi-wallet' },
    { id: 'journey',      label: 'Journey',      icon: 'pi pi-history' },
    { id: 'quality',      label: 'Quality',      icon: 'pi pi-star' },
    { id: 'profile',      label: 'Profile',      icon: 'pi pi-user' },
    { id: 'documents',    label: 'Documents',    icon: 'pi pi-folder' },
  ];
  private readonly _actionsSeenRevisionBySession = signal<Record<string, number>>({});
  private _actionsReadCommitTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _aiInsightsSeenRevisionBySession = signal<Record<string, number>>({});
  private _aiInsightsReadCommitTimer: ReturnType<typeof setTimeout> | null = null;

  readonly actionsTabHasUpdate = computed(() => {
    const sessionId = this.clientSessionService.selectedSession()?.uiSessionId ?? null;
    if (!sessionId) return false;
    const seenRevision = this._actionsSeenRevisionBySession()[sessionId] ?? 0;
    const currentRevision = this._actionsState.storyRevision(sessionId);
    return currentRevision > seenRevision && this.activeTab() !== 'actions';
  });

  readonly aiInsightsTabHasUpdate = computed(() => {
    const sessionId = this.clientSessionService.selectedSession()?.uiSessionId ?? null;
    if (!sessionId) return false;
    const seenRevision = this._aiInsightsSeenRevisionBySession()[sessionId] ?? 0;
    const currentRevision = this._aiInsightsState.storyRevision(sessionId);
    return currentRevision > seenRevision && this.activeTab() !== 'ai-insights';
  });

  readonly tabsForDisplay = computed<TabItem[]>(() => {
    const hasActionsUpdate = this.actionsTabHasUpdate();
    const hasInsightsUpdate = this.aiInsightsTabHasUpdate();
    return this.allTabs.map((tab) => {
      if (tab.id === 'actions') return { ...tab, hasUpdate: hasActionsUpdate };
      if (tab.id === 'ai-insights') return { ...tab, hasUpdate: hasInsightsUpdate };
      return tab;
    });
  });

  private _lastSelectedSessionId: string | null = null;

  /** Explicit height for splitter; same pattern as lib-chat and client-list. */
  readonly splitterHeight = MY_CLIENTS_SPLITTER_HEIGHT;

  /** Menu items for the right copilot pane header. Includes the collapse/expand chevron on the right. */
  readonly copilotPaneMenuItems = computed<MenuItem[]>(() => {
    const collapsed = this.copilotCollapsed();
    return [
      {
        label: 'BrightAI Copilot',
        icon: 'pi pi-microchip-ai',
        styleClass: 'bai-tab-menubar-item-active',
      },
      {
        icon: collapsed ? 'pi pi-angle-left' : 'pi pi-angle-right',
        styleClass: 'bai-copilot-collapse-btn ml-auto',
        command: () => this.toggleCopilot(),
        title: collapsed ? 'Expand BrightAI Copilot' : 'Collapse BrightAI Copilot',
      },
    ];
  });

  toggleCopilot(): void {
    this.layoutService.toggleCopilotCollapsed();
  }

  constructor() {
    effect(() => {
      const selectedSession = this.clientSessionService.selectedSession();
      if (!selectedSession) {
        if (!this.clientSessionService.ensureOneClientSelected()) {
          this._copilotFacadeService.clearContext();
        }
      }
    });
    effect(() => {
      const session = this.clientSessionService.selectedSession();
      const sessionId = session?.uiSessionId ?? null;
      if (sessionId !== this._lastSelectedSessionId && sessionId != null) {
        this._lastSelectedSessionId = sessionId;
        this.activeTab.set('client-chat');
      } else if (sessionId != null) {
        this._lastSelectedSessionId = sessionId;
      }
    });

    // Story Service -> Actions and AI Insights bridge.
    // Keeps both tabs current even when they are not open.
    effect(() => {
      const storyFacade = this._storyFacade;
      if (!storyFacade) return;
      const sessionId = this.clientSessionService.selectedSession()?.uiSessionId ?? null;
      const isAvailable = storyFacade.isAvailable();
      const mode = storyFacade.mode();
      const payload = storyFacade.currentPayload();
      const demoId = storyFacade.demoId();
      const confirmedStep = storyFacade.confirmedStep();
      const isStoryDemoMode = isAvailable && mode === 'story-demo';

      untracked(() => {
        if (!sessionId) return;
        if (isStoryDemoMode) {
          const stepKey = `${demoId}:${confirmedStep}`;
          this._actionsState.applyStepPayload(sessionId, payload, stepKey);
          this._aiInsightsState.applyStepPayload(sessionId, payload, stepKey);
          return;
        }
        this._actionsState.loadFallback(sessionId);
        this._aiInsightsState.loadFallback(sessionId);
      });
    });

    // Visiting Actions marks current Story opportunity revision as read for tab-dot UX.
    effect(() => {
      const sessionId = this.clientSessionService.selectedSession()?.uiSessionId ?? null;
      const activeTab = this.activeTab();
      if (activeTab !== 'actions' && this._actionsReadCommitTimer) {
        clearTimeout(this._actionsReadCommitTimer);
        this._actionsReadCommitTimer = null;
      }
      if (!sessionId || activeTab !== 'actions') return;
      const currentRevision = this._actionsState.storyRevision(sessionId);
      const seenRevision = this._actionsSeenRevisionBySession()[sessionId] ?? 0;
      if (currentRevision <= seenRevision) return;

      if (this._actionsReadCommitTimer) {
        clearTimeout(this._actionsReadCommitTimer);
      }
      this._actionsReadCommitTimer = setTimeout(() => {
        this._actionsReadCommitTimer = null;
        const stillSelectedSessionId =
          this.clientSessionService.selectedSession()?.uiSessionId ?? null;
        const stillActiveTab = this.activeTab();
        if (stillSelectedSessionId !== sessionId || stillActiveTab !== 'actions') {
          return;
        }
        const latestRevision = this._actionsState.storyRevision(sessionId);
        untracked(() => {
          this._actionsSeenRevisionBySession.update((prev) => ({
            ...prev,
            [sessionId]: latestRevision,
          }));
        });
      }, 0);
    });

    // Visiting AI Insights marks current story revision as read for tab-dot UX.
    effect(() => {
      const sessionId = this.clientSessionService.selectedSession()?.uiSessionId ?? null;
      const activeTab = this.activeTab();
      if (activeTab !== 'ai-insights' && this._aiInsightsReadCommitTimer) {
        clearTimeout(this._aiInsightsReadCommitTimer);
        this._aiInsightsReadCommitTimer = null;
      }
      if (!sessionId || activeTab !== 'ai-insights') return;
      const currentRevision = this._aiInsightsState.storyRevision(sessionId);
      const seenRevision = this._aiInsightsSeenRevisionBySession()[sessionId] ?? 0;
      if (currentRevision <= seenRevision) return;

      // Mark read only after render has settled, not immediately on tab selection.
      // This keeps unread semantics aligned with "viewed content" rather than click intent.
      if (this._aiInsightsReadCommitTimer) {
        clearTimeout(this._aiInsightsReadCommitTimer);
      }
      this._aiInsightsReadCommitTimer = setTimeout(() => {
        this._aiInsightsReadCommitTimer = null;
        const stillSelectedSessionId =
          this.clientSessionService.selectedSession()?.uiSessionId ?? null;
        const stillActiveTab = this.activeTab();
        if (stillSelectedSessionId !== sessionId || stillActiveTab !== 'ai-insights') {
          return;
        }
        const latestRevision = this._aiInsightsState.storyRevision(sessionId);
        untracked(() => {
          this._aiInsightsSeenRevisionBySession.update((prev) => ({
            ...prev,
            [sessionId]: latestRevision,
          }));
        });
      }, 0);
    });
  }

  ngOnDestroy(): void {
    if (this._actionsReadCommitTimer) {
      clearTimeout(this._actionsReadCommitTimer);
      this._actionsReadCommitTimer = null;
    }
    if (this._aiInsightsReadCommitTimer) {
      clearTimeout(this._aiInsightsReadCommitTimer);
      this._aiInsightsReadCommitTimer = null;
    }
  }

  private resolveStoryFacade(): StoryDemoFacade | null {
    try {
      return this._injector.get(StoryDemoFacade);
    } catch {
      return null;
    }
  }

  ngOnInit() {
    this._copilotFacadeService.hideInput.set(false);
    // Deep-link support: if a notification navigated here with ?notifTab=<tab>,
    // activate that tab and clear the query param so back-navigation is clean.
    const notifTab = this._route.snapshot.queryParamMap.get('notifTab') as MyClientsTabId | null;
    if (notifTab) {
      const validTabs: MyClientsTabId[] = ['client-chat', 'actions', 'ai-insights', 'resources', 'finances', 'accounts', 'journey', 'quality', 'profile', 'documents'];
      if (validTabs.includes(notifTab)) {
        this.activeTab.set(notifTab);
      }
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { notifTab: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }
}
